/* v3.8 · Global Weather / Shrinkage-Crack Alert Banner.
 *
 * Renders a bright red sticky banner at the top of the surrounding shell when
 * current plant conditions cross ANY of these thresholds:
 *     temperature > 35°C   ·   humidity < 30%   ·   wind > 30 km/h
 *
 * Same thresholds and copy as the Customer Portal ordering warning
 * (see /app/backend/utils/weather.py). Silent when conditions are safe or the
 * upstream API is unreachable, so it never adds visual noise on calm days.
 *
 * Usage:
 *   <WeatherAlertBanner scope="staff" />          // Admin + Dispatch (AppShell)
 *   <WeatherAlertBanner scope="portal" />         // Customer Portal (PortalShell)
 *
 * Dismissable per session (sessionStorage) so an operator who has acknowledged
 * the risk isn't nagged on every page change, but the banner returns on the
 * next browser tab / login.
 */
import { useEffect, useState, useCallback } from "react";
import { AlertTriangle, X, Wind, Droplets, Thermometer, Eye } from "lucide-react";
import { api } from "@/lib/api";

const DISMISS_KEY = "gadtech.weatherAlert.dismissed";

export default function WeatherAlertBanner({ scope = "staff" }) {
  const [wx, setWx] = useState(null);           // {has_risk, reasons, message, temp_c, humidity_pct, wind_kph}
  const [dismissed, setDismissed] = useState(false);

  const endpoint = scope === "portal" ? "/portal/weather-alert" : "/weather";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(endpoint);
      setWx(data);
    } catch {
      // Silently ignore — never let a weather failure block the app.
      setWx(null);
    }
  }, [endpoint]);

  useEffect(() => {
    // Session-scoped dismiss. Clearing sessionStorage or opening a new tab
    // re-arms the banner.
    try {
      const raw = sessionStorage.getItem(DISMISS_KEY);
      if (raw) {
        const { at, reasons } = JSON.parse(raw);
        // Auto re-arm after 1 hour even inside a live session — conditions
        // can worsen and we don't want to stay silent all afternoon.
        if (Date.now() - at < 60 * 60 * 1000) {
          setDismissed({ reasons });
        }
      }
    } catch { /* ignore */ }
    load();
    const t = setInterval(load, 15 * 60 * 1000); // v3.11 · efficiency pass — refresh every 15 min (was 5)
    return () => clearInterval(t);
  }, [load]);

  if (!wx || !wx.has_risk) return null;
  const watch = wx.level === "watch";
  // If the user dismissed a banner that carried the SAME reasons, keep it hidden.
  // If conditions changed (new reason surfaced), pop it back up.
  if (dismissed && Array.isArray(dismissed.reasons)) {
    const same = dismissed.reasons.length === (wx.reasons || []).length &&
                 dismissed.reasons.every((r) => (wx.reasons || []).includes(r));
    if (same) return null;
  }

  const dismiss = () => {
    try {
      sessionStorage.setItem(
        DISMISS_KEY,
        JSON.stringify({ at: Date.now(), reasons: wx.reasons || [] }),
      );
    } catch { /* ignore */ }
    setDismissed({ reasons: wx.reasons || [] });
  };

  // Conditions are fine now and the rough patch is still hours off — a quiet line, not a siren.
  if (watch) {
    return (
      <div data-testid="weather-watch-note" className="w-full bg-zinc-800 border-b border-zinc-700 text-zinc-300">
        <div className="px-4 sm:px-6 py-1.5 flex items-center gap-3 text-[11px] mono uppercase tracking-widest">
          <Eye className="w-3.5 h-3.5 shrink-0 text-zinc-400" aria-hidden />
          <span className="text-zinc-400">Pour conditions · watch</span>
          {wx.worst && (
            <span className="text-zinc-200" data-testid="weather-watch-peak">
              {wx.worst.at} · {Math.round(wx.worst.temperature_c)}°C · {Math.round(wx.worst.humidity_pct)}% RH · gusts {Math.round(wx.worst.gust_kmh || wx.worst.wind_kmh || 0)} km/h · E {wx.worst.evap_kg_m2_h}
            </span>
          )}
          <span className="hidden sm:inline text-zinc-500 truncate">fine now — we'll go red if it's still on in {wx.thresholds?.lead_hours ?? 3} h</span>
          <button type="button" onClick={dismiss} data-testid="weather-watch-dismiss" className="ml-auto shrink-0 text-zinc-500 hover:text-white"><X className="w-3.5 h-3.5" /></button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="weather-alert-banner"
      className={`w-full text-white border-b-2 shadow-md relative ${wx.level === "caution" ? "bg-amber-600 border-amber-800" : "bg-red-600 border-red-800"}`}
      role="alert"
    >
      <div className="max-w-full px-4 sm:px-6 py-2.5 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 animate-pulse" aria-hidden />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span
              className={`mono text-[10px] uppercase tracking-[0.24em] font-black bg-white px-1.5 py-0.5 ${wx.level === "caution" ? "text-amber-700" : "text-red-700"}`}
              data-testid="weather-alert-tag"
            >
              {wx.level === "caution" ? "Pour Conditions · Caution" : "Extreme Pour Conditions"}
            </span>
            <span className="text-sm font-bold" data-testid="weather-alert-title">
              Shrinkage-Crack Risk {wx.level === "caution" ? "· caution" : "· take precautions"}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            {wx.temp_c != null && (
              <span className="inline-flex items-center gap-1" data-testid="weather-alert-temp">
                <Thermometer className="w-3.5 h-3.5" /> {Math.round(wx.temp_c)}°C
              </span>
            )}
            {wx.humidity_pct != null && (
              <span className="inline-flex items-center gap-1" data-testid="weather-alert-humidity">
                <Droplets className="w-3.5 h-3.5" /> {Math.round(wx.humidity_pct)}% RH
              </span>
            )}
            {wx.wind_kph != null && (
              <span className="inline-flex items-center gap-1" data-testid="weather-alert-wind">
                <Wind className="w-3.5 h-3.5" /> {Math.round(wx.wind_kph)} km/h{wx.gust_kph != null ? ` · gusts ${Math.round(wx.gust_kph)}` : ""}
              </span>
            )}
            {wx.evap_now != null && (
              <span className="inline-flex items-center gap-1 mono" data-testid="weather-alert-evap-now" title={`ACI 305 evaporation rate now (kg/m²/h) — caution ≥${wx.thresholds?.evap_caution ?? 0.75}, high ≥${wx.thresholds?.evap_high ?? 1.0}. Mean wind, not gusts.`}>
                E {wx.evap_now} kg/m²/h now
              </span>
            )}
            {wx.worst && (
              <span className="inline-flex items-center gap-1 font-bold" data-testid="weather-alert-worst">
                Peak {wx.worst.at}: gusts {Math.round(wx.worst.gust_kmh || wx.worst.wind_kmh || 0)} km/h · {Math.round(wx.worst.humidity_pct)}% RH · E {wx.worst.evap_kg_m2_h}
              </span>
            )}
          </div>
          {(wx.reasons || []).length > 0 && (
            <div
              className="mt-1 text-[11px] text-red-50/95 leading-snug"
              data-testid="weather-alert-reasons"
            >
              {wx.reasons.join(" · ")}
            </div>
          )}
          {scope === "portal" && wx.message && (
            <div className="mt-1 text-[11px] text-red-50 leading-snug italic" data-testid="weather-alert-message">
              {wx.message}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          data-testid="weather-alert-dismiss"
          title="Dismiss for this session"
          className="shrink-0 mt-0.5 rounded-none border border-white/50 hover:bg-white hover:text-red-700 transition-colors p-1"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
